<template>
  <a-select
    v-element-value="fusionInputCheckValue"
    v-fusioninputcheck="fusionInputCheckBinding"
    :value="selectValue"
    class="hkl-select"
    :class="{
      'hkl-select_right-align': align === 'right',
      'hkl-select_single-taggable-input': singleTaggableInput,
      'hkl-select_multiple': isMultipleSelect,
      'hkl-select_multiple-has-value': isMultipleSelectNotEmpty,
      'hkl-select_multiple-search-empty': isMultipleSearchEmpty,
    }"
    optionFilterProp="title"
    :showArrow="showArrow"
    :getPopupContainer="resolvedGetPopupContainer"
    :dropdownClassName="mergedDropdownClassName"
    :dropdownStyle="mergedDropdownStyle"
    v-bind="selectAttrs"
    v-on="selectListeners"
    @change="onSelectChange"
    @select="onSelect"
    @search="onSelectSearch"
    @focus="onSelectFocus"
    @blur="onSelectBlur"
    @dropdownVisibleChange="onDropdownVisibleChange"
    @keydown.native.capture="onSelectKeydownCapture"
  >
    <template
      v-for="(option, index) in options"
    >
      <a-select-opt-group
        v-if="isOptionGroup(option) && option.options.length"
        :key="`group-${index}`"
        :label="option.name"
        :title="option.name"
      >
        <a-select-option
          v-for="o, i in option.options"
          :key="`${index}-${i}`"
          :value="getOptionValue(o)"
          :label="getOptionSelectedLabel(o)"
          :title="getOptionTitle(o)"
          :disabled="isOptionDisabled(o)"
          :class="{
            'hkl-select__tag-option': isSingleTaggableInputTagOption(o),
            'hkl-select__display-only-option': isDisplayOnlyOption(o),
          }"
        >
          <span
            v-if="getOptionHint(o)"
            class="hkl-select__option-with-hint"
          >
            <span class="hkl-select__option-label">{{ getOptionTitle(o) }}</span>
            <span class="hkl-select__option-hint">{{ getOptionHint(o) }}</span>
          </span>
          <span
            v-else
            v-text="getOptionTitle(o)"
          />
        </a-select-option>
      </a-select-opt-group>
      <a-select-option
        v-else-if="!isOptionGroup(option)"
        :key="`option-${index}`"
        :value="getOptionValue(option)"
        :label="getOptionSelectedLabel(option)"
        :title="getOptionTitle(option)"
        :disabled="isOptionDisabled(option)"
        :class="{
          'hkl-select__tag-option': isSingleTaggableInputTagOption(option),
          'hkl-select__display-only-option': isDisplayOnlyOption(option),
        }"
      >
        <span
          v-if="getOptionHint(option)"
          class="hkl-select__option-with-hint"
        >
          <span class="hkl-select__option-label">{{ getOptionTitle(option) }}</span>
          <span class="hkl-select__option-hint">{{ getOptionHint(option) }}</span>
        </span>
        <span
          v-else
          v-text="getOptionTitle(option)"
        />
      </a-select-option>
    </template>
  </a-select>
</template>
<script>
import { Select } from 'ant-design-vue'
import renderEmpty from 'ant-design-vue/lib/config-provider/renderEmpty'
import {
  MAX_VISIBLE_DROPDOWN_OPTIONS,
  dropdownViewportMaxHeightPx,
  scrollDropdownViewportMaxHeightPx,
} from './HKLMultiSelect.const'

export const SOURCE = 'value'
export const EVENT = 'change'
export const SINGLE_TAGGABLE_INPUT_TAG_PREFIX = '__HKL_SELECT_TAG__:'
let dropdownOptionPointerGuardReferences = 0
const defaultGetPopupContainer = (triggerNode) => triggerNode.parentNode

function stopClippedDropdownOptionPointer (event) {
  const target = event && event.target
  const option = target && target.closest
    ? target.closest('.ant-select-dropdown-menu-item')
    : null
  const dropdown = option && option.closest
    ? option.closest('.ant-select-dropdown.hkl-select-dropdown')
    : null
  const menu = dropdown && dropdown.querySelector
    ? dropdown.querySelector('.ant-select-dropdown-menu, .ant-select-dropdown-menu-root')
    : null
  if (!menu || !option) return

  const hasScrollableOverflow = menu.scrollHeight > menu.clientHeight + 1
  if (!hasScrollableOverflow) return

  const menuRect = menu.getBoundingClientRect()
  const optionRect = option.getBoundingClientRect()
  const tolerancePx = 1
  const isClipped = optionRect.top < menuRect.top - tolerancePx ||
    optionRect.bottom > menuRect.bottom + tolerancePx
  if (!isClipped) return

  event.preventDefault()
  event.stopPropagation()
  if (event.stopImmediatePropagation) {
    event.stopImmediatePropagation()
  }
}

/**
 * @type {import('vue').Component}
 */
export const HKLSelect = {
  name: 'HKLSelect',
  model: {
    prop: SOURCE,
    event: EVENT,
  },
  components: {
    Select,
  },
  data () {
    return {
      singleTaggableInputValue: this.getSingleTaggableInputValue(this[SOURCE]),
      inputLikeDropdownOpen: false,
      inputLikeOptionSelected: false,
      inputLikeOptionSelectedValue: undefined,
      dropdownWidth: 0,
      selectSearchValue: '',
    }
  },
  props: {
    options: {
      type: Array,
      default: () => [],
    },
    getPopupContainer: {
      type: Function,
      default: defaultGetPopupContainer,
    },
    [SOURCE]: {
      type: [Array, String, Number, Boolean],
      default: undefined,
    },
    align: {
      type: String,
      default: 'left',
      validator: (align) => ['left', 'right'].includes(align),
    },
    maxVisibleOptions: {
      type: Number,
      default: MAX_VISIBLE_DROPDOWN_OPTIONS,
    },
    required: {
      type: Boolean,
      default: false,
    },
    showTip: {
      type: Boolean,
      default: true,
    },
    fusionInputCheckPattern: {
      type: Function,
      default: () => true,
    },
    respectOptionDisabled: {
      type: Boolean,
      default: false,
    },
    singleTaggableInput: {
      type: Boolean,
      default: false,
    },
    openOnFocus: {
      type: Boolean,
      default: false,
    },
    showArrow: {
      type: Boolean,
      default: true,
    },
    hideNoDataDropdown: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    selectAttrs () {
      const {
        dropdownClassName,
        dropdownStyle,
        defaultActiveFirstOption,
        ...rest
      } = this.$attrs
      const attrs = { ...rest }
      delete attrs.open
      delete attrs.showArrow
      const resolvedMode = this.singleTaggableInput
        ? Select.SECRET_COMBOBOX_MODE_DO_NOT_USE
        : this.$attrs.mode
      if (resolvedMode) {
        attrs.mode = resolvedMode
      }
      if (this.shouldPassDropdownOpen) {
        attrs.open = this.controlledDropdownOpen
      }
      if (this.singleTaggableInput && defaultActiveFirstOption === undefined) {
        attrs.defaultActiveFirstOption = false
      } else if (defaultActiveFirstOption !== undefined) {
        attrs.defaultActiveFirstOption = defaultActiveFirstOption
      }
      if (this.singleTaggableInput && attrs.optionLabelProp === undefined) {
        attrs.optionLabelProp = 'title'
      }
      if (this.singleTaggableInput && attrs.notFoundContent === undefined) {
        attrs.notFoundContent = renderEmpty(this.$createElement, 'Select')
      }
      if (this.hideNoDataDropdown &&
        attrs.notFoundContent === undefined &&
        !String(this.selectSearchValue || '').trim()
      ) {
        attrs.notFoundContent = null
      }
      if (this.singleTaggableInput && attrs.filterOption === undefined) {
        attrs.filterOption = this.filterSingleTaggableOption
      }
      return attrs
    },
    selectListeners () {
      const listeners = { ...this.$listeners }
      delete listeners[EVENT]
      delete listeners.select
      delete listeners.search
      delete listeners.focus
      delete listeners.blur
      delete listeners.dropdownVisibleChange
      if (this.singleTaggableInput) {
        delete listeners.input
      }
      return listeners
    },
    resolvedMode () {
      if (this.singleTaggableInput) {
        return Select.SECRET_COMBOBOX_MODE_DO_NOT_USE
      }
      return this.$attrs.mode
    },
    selectValue () {
      return this.singleTaggableInput ? this.singleTaggableInputValue : this.proxyValue
    },
    shouldControlInputLikeDropdownOpen () {
      return this.singleTaggableInput && typeof this.$attrs.open !== 'boolean'
    },
    shouldPassDropdownOpen () {
      return this.shouldControlInputLikeDropdownOpen || typeof this.$attrs.open === 'boolean'
    },
    isMultipleSearchEmpty () {
      return this.resolvedMode === 'multiple' && this.$attrs.showSearch !== false && !String(this.selectSearchValue || '').trim()
    },
    isMultipleSelect () {
      return this.resolvedMode === 'multiple'
    },
    isMultipleSelectNotEmpty () {
      if (!this.isMultipleSelect) return false
      if (Array.isArray(this.selectValue)) return this.selectValue.length > 0
      return this.selectValue !== undefined && this.selectValue !== null && this.selectValue !== ''
    },
    fusionInputCheckValue () {
      if (Array.isArray(this.selectValue)) return this.selectValue
      if (this.selectValue === undefined || this.selectValue === null) return ''
      return String(this.selectValue)
    },
    fusionInputCheckBinding () {
      return {
        type: 'string',
        tip: this.$t('hint.inputTip.string'),
        showTip: this.showTip,
        required: this.required,
        pattern: this.fusionInputCheckPattern,
      }
    },
    controlledDropdownOpen () {
      if (this.shouldControlInputLikeDropdownOpen) {
        return this.inputLikeDropdownOpen
      }
      return typeof this.$attrs.open === 'boolean' ? this.$attrs.open : undefined
    },
    resolvedGetPopupContainer () {
      return this.getPopupContainer
    },
    mergedDropdownClassName () {
      const fromAttrs = this.$attrs.dropdownClassName
      const classes = ['hkl-select-dropdown']
      if (fromAttrs) {
        classes.push(fromAttrs)
      }
      if (this.$attrs.dropdownMatchSelectWidth === false) {
        classes.push('hkl-select-dropdown--no-match-width')
      }
      return classes.join(' ')
    },
    mergedDropdownStyle () {
      const fromAttrs = this.$attrs.dropdownStyle || {}
      const count = this.countDropdownRows()
      const visibleRows = count > 0
        ? Math.min(count, this.maxVisibleOptions)
        : this.maxVisibleOptions
      const needsScroll = count > this.maxVisibleOptions
      const maxHeight = needsScroll
        ? scrollDropdownViewportMaxHeightPx(visibleRows)
        : dropdownViewportMaxHeightPx(visibleRows)
      const mergedStyle = {
        ...fromAttrs,
        '--hkl-select-dropdown-max-height': `${maxHeight}px`,
        '--hkl-select-dropdown-overflow-y': needsScroll ? 'auto' : 'hidden',
      }

      const explicitWidth = fromAttrs.width !== undefined
      const explicitMinWidth = fromAttrs.minWidth !== undefined
      const explicitMaxWidth = fromAttrs.maxWidth !== undefined
      if (this.dropdownWidth > 0 && this.$attrs.dropdownMatchSelectWidth !== false && !explicitWidth && !explicitMinWidth && !explicitMaxWidth) {
        mergedStyle.width = `${this.dropdownWidth}px`
      }

      return mergedStyle
    },
    proxyValue: {
      get () {
        return this[SOURCE]
      },
      set (newValue) {
        this.emitChange(newValue)
      },
    },
  },
  watch: {
    [SOURCE] () {
      this.syncSingleTaggableInputValue()
      this.syncLockedSelectedChoices()
    },
    options: {
      deep: true,
      handler () {
        this.syncLockedSelectedChoices()
        if (this.hideNoDataDropdown && this.shouldControlInputLikeDropdownOpen && !this.hasDropdownOptionsForInput(this.singleTaggableInputValue)) {
          this.inputLikeDropdownOpen = false
        }
      },
    },
    respectOptionDisabled () {
      this.syncLockedSelectedChoices()
    },
    singleTaggableInput () {
      this.syncSingleTaggableInputValue()
    },
  },
  mounted () {
    this.addDropdownOptionPointerGuard()
    this.syncDropdownWidth()
    this.syncLockedSelectedChoices()
  },
  updated () {
    this.syncDropdownWidth()
    this.syncLockedSelectedChoices()
  },
  beforeDestroy () {
    this.removeDropdownOptionPointerGuard()
    this.dropdownWidth = 0
  },
  methods: {
    addDropdownOptionPointerGuard () {
      if (typeof document === 'undefined') return
      if (dropdownOptionPointerGuardReferences === 0) {
        document.addEventListener('mouseenter', stopClippedDropdownOptionPointer, true)
        document.addEventListener('mousemove', stopClippedDropdownOptionPointer, true)
        document.addEventListener('mouseover', stopClippedDropdownOptionPointer, true)
      }
      dropdownOptionPointerGuardReferences += 1
    },
    removeDropdownOptionPointerGuard () {
      if (typeof document === 'undefined' || dropdownOptionPointerGuardReferences === 0) return
      dropdownOptionPointerGuardReferences -= 1
      if (dropdownOptionPointerGuardReferences === 0) {
        document.removeEventListener('mouseenter', stopClippedDropdownOptionPointer, true)
        document.removeEventListener('mousemove', stopClippedDropdownOptionPointer, true)
        document.removeEventListener('mouseover', stopClippedDropdownOptionPointer, true)
      }
    },
    getFlatOptions () {
      return (this.options || []).reduce((list, option) => {
        if (this.isOptionGroup(option)) {
          return list.concat(option.options)
        }
        return list.concat(option)
      }, []).filter(option => !this.isDisplayOnlyOption(option))
    },
    countFlatOptions () {
      return this.getFlatOptions().length
    },
    countDropdownRows () {
      return (this.options || []).reduce((count, option) => {
        if (this.isOptionGroup(option)) {
          if (!option.options.length) return count
          return count + 1 + option.options.filter(item => !this.isDisplayOnlyOption(item)).length
        }
        return this.isDisplayOnlyOption(option) ? count : count + 1
      }, 0)
    },
    isOptionGroup (option) {
      return option && Array.isArray(option.options)
    },
    syncDropdownWidth () {
      const root = this.$el
      if (!root) return

      const rect = root.getBoundingClientRect()
      const width = Math.round((rect && rect.width) || root.offsetWidth || 0)
      if (width > 0 && width !== this.dropdownWidth) {
        this.dropdownWidth = width
      }
    },
    getOptionValue (option) {
      if (typeof option === 'string') return option
      if (this.isSingleTaggableInputTagOption(option)) {
        return `${SINGLE_TAGGABLE_INPUT_TAG_PREFIX}${String(option.value || option.name || '')}`
      }
      let value = option.value
      if (typeof value === 'object') {
        value = JSON.stringify(value)
      }
      return value
    },
    getOptionTitle (option) {
      if (typeof option === 'string') return option
      return option.name
    },
    getOptionSelectedLabel (option) {
      if (typeof option === 'string') return option
      return option.hklSelectSelectedLabel || this.getOptionTitle(option)
    },
    getOptionHint (option) {
      if (!option || typeof option === 'string') return ''
      return option.hklSelectHint || ''
    },
    getVNodeProp (node, propName) {
      const propsData = (node && node.componentOptions && node.componentOptions.propsData) || {}
      const attrs = (node && node.data && node.data.attrs) || {}
      if (propName in propsData) return propsData[propName]
      return attrs[propName]
    },
    isSingleTaggableInputTagOption (option) {
      return option && typeof option !== 'string' && option.isSingleTaggableInputTag === true
    },
    isDisplayOnlyOption (option) {
      return option && typeof option !== 'string' && option.hklSelectDisplayOnly === true
    },
    isSingleTaggableInputTagValue (value) {
      return typeof value === 'string' && value.indexOf(SINGLE_TAGGABLE_INPUT_TAG_PREFIX) === 0
    },
    filterSingleTaggableOption (input, option) {
      const keyword = String(input || '').trim().toLowerCase()
      const optionValue = this.getVNodeProp(option, 'value')
      if (this.isSingleTaggableInputTagValue(optionValue)) {
        return !!keyword
      }
      if (this.getVNodeProp(option, 'disabled')) return false
      const optionTitle = this.getVNodeProp(option, 'title')
      const target = optionTitle != null ? optionTitle : optionValue
      return String(target || '').toLowerCase().indexOf(keyword) > -1
    },
    getSingleTaggableValue (value) {
      const normalized = Array.isArray(value) ? value[value.length - 1] : value
      if (this.isSingleTaggableInputTagValue(normalized)) {
        return normalized.slice(SINGLE_TAGGABLE_INPUT_TAG_PREFIX.length)
      }
      return normalized
    },
    getSingleTaggableInputValue (value) {
      const normalized = this.getSingleTaggableValue(value)
      return normalized || normalized === 0 ? String(normalized) : ''
    },
    syncSingleTaggableInputValue () {
      if (this.singleTaggableInput) {
        this.singleTaggableInputValue = this.getSingleTaggableInputValue(this[SOURCE])
      }
    },
    commitSingleTaggableInputValue (value, option, forceCommit = false) {
      const normalized = String(this.getSingleTaggableValue(value) || '').trim()
      const current = this.getSingleTaggableInputValue(this[SOURCE])
      if (!normalized) {
        if (current) {
          this.singleTaggableInputValue = ''
          this.emitChange('', option)
        } else {
          this.syncSingleTaggableInputValue()
        }
        return
      }
      if (!forceCommit && normalized === current) return
      this.singleTaggableInputValue = normalized
      this.emitChange(normalized, option)
    },
    isOptionLocked (option) {
      return this.respectOptionDisabled && option && typeof option !== 'string' &&
        (option.removeDisabled === true || option.disabled === true)
    },
    isOptionDisabled (option) {
      return this.isDisplayOnlyOption(option) || this.isOptionLocked(option)
    },
    getLockedOptionValues () {
      return this.getFlatOptions()
        .filter(option => this.isOptionLocked(option))
        .map(option => this.getOptionValue(option))
    },
    getLockedOptionTitles () {
      return this.getFlatOptions()
        .filter(option => this.isOptionLocked(option))
        .map(option => String(this.getOptionTitle(option)))
    },
    getChangeValueWithLockedOptions (newValue) {
      if (!this.respectOptionDisabled || !Array.isArray(newValue) || !Array.isArray(this[SOURCE])) {
        return newValue
      }

      const lockedValues = this.getLockedOptionValues()
      if (!lockedValues.length) return newValue

      const restoredValues = []
      const currentValues = this[SOURCE] || []
      currentValues.forEach(value => {
        if (newValue.includes(value) || lockedValues.includes(value)) {
          restoredValues.push(value)
        }
      })
      newValue.forEach(value => {
        if (!restoredValues.includes(value)) {
          restoredValues.push(value)
        }
      })
      return this.isSameArrayValue(restoredValues, newValue) ? newValue : restoredValues
    },
    isSameArrayValue (first, second) {
      if (!Array.isArray(first) || !Array.isArray(second)) return first === second
      return first.length === second.length && first.every((value, index) => value === second[index])
    },
    onSelectChange (newValue, option) {
      this.selectSearchValue = ''
      if (this.singleTaggableInput) {
        this.singleTaggableInputValue = this.getSingleTaggableInputValue(newValue)
        const optionSelected = this.consumeInputLikeOptionSelected(newValue)
        if (this.shouldControlInputLikeDropdownOpen) {
          this.inputLikeDropdownOpen = optionSelected
            ? false
            : this.shouldOpenInputLikeDropdown(this.singleTaggableInputValue)
        }
        if (!optionSelected) {
          this.$emit('input', this.singleTaggableInputValue)
        }
        return
      }
      this.emitChange(newValue, option)
    },
    onSelect (value, option) {
      const nextValue = this.singleTaggableInput ? this.getSingleTaggableValue(value) : value
      if (this.singleTaggableInput) {
        this.inputLikeOptionSelected = true
        this.inputLikeOptionSelectedValue = this.getSingleTaggableInputValue(nextValue)
        this.commitSingleTaggableInputValue(nextValue, option, true)
        if (this.shouldControlInputLikeDropdownOpen) {
          this.inputLikeDropdownOpen = false
        }
      }
      this.$emit('select', nextValue, option)
    },
    onSelectSearch (value) {
      this.selectSearchValue = value
      if (this.shouldControlInputLikeDropdownOpen) {
        this.inputLikeDropdownOpen = this.shouldOpenInputLikeDropdown(value)
      }
      this.$emit('search', value)
    },
    onSelectFocus (value) {
      if (this.singleTaggableInput && this.openOnFocus) {
        this.inputLikeOptionSelected = false
        this.inputLikeDropdownOpen = this.hideNoDataDropdown
          ? this.hasDropdownOptionsForInput(this.singleTaggableInputValue)
          : true
      }
      this.$emit('focus', value)
    },
    onSelectBlur (value) {
      this.selectSearchValue = ''
      if (this.singleTaggableInput) {
        this.commitSingleTaggableInputValue(this.singleTaggableInputValue, undefined)
      }
      if (this.shouldControlInputLikeDropdownOpen) {
        this.inputLikeDropdownOpen = false
      }
      this.inputLikeOptionSelected = false
      this.inputLikeOptionSelectedValue = undefined
      this.$emit('blur', value)
    },
    onDropdownVisibleChange (open) {
      this.$nextTick(() => {
        this.syncDropdownWidth()
      })

      if (this.shouldControlInputLikeDropdownOpen) {
        this.inputLikeDropdownOpen = open && this.inputLikeDropdownOpen &&
          (this.openOnFocus || !!String(this.singleTaggableInputValue || '').trim())
        this.$emit('dropdownVisibleChange', this.inputLikeDropdownOpen)
        return
      }
      this.$emit('dropdownVisibleChange', open)
    },
    shouldOpenInputLikeDropdown (value) {
      const shouldOpen = this.openOnFocus || !!String(value || '').trim()
      if (!shouldOpen) return false
      return this.hideNoDataDropdown ? this.hasDropdownOptionsForInput(value) : true
    },
    hasDropdownOptionsForInput (value) {
      const keyword = String(value || '').trim().toLowerCase()
      return this.getFlatOptions().some(option => {
        if (!option || this.isSingleTaggableInputTagOption(option)) return false
        if (!keyword) return true
        const title = this.getOptionTitle(option)
        const optionValue = this.getOptionValue(option)
        return [title, optionValue].some(target =>
          String(target || '').toLowerCase().indexOf(keyword) > -1,
        )
      })
    },
    consumeInputLikeOptionSelected (newValue) {
      const selectedValue = this.inputLikeOptionSelectedValue
      const nextValue = this.getSingleTaggableInputValue(newValue)
      const selected = this.inputLikeOptionSelected
      if (!selected || selectedValue !== nextValue) {
        return false
      }
      this.inputLikeOptionSelected = false
      this.inputLikeOptionSelectedValue = undefined
      return true
    },
    onSelectKeydownCapture (event) {
      if (!this.shouldBlockLockedKeyboardDelete(event)) return
      event.preventDefault()
      event.stopPropagation()
      if (event.stopImmediatePropagation) {
        event.stopImmediatePropagation()
      }
      this.syncLockedSelectedChoices()
    },
    shouldBlockLockedKeyboardDelete (event) {
      if (!this.respectOptionDisabled || !Array.isArray(this[SOURCE])) return false

      const isDeleteKey = event.key === 'Backspace' || event.key === 'Delete' ||
        event.keyCode === 8 || event.keyCode === 46
      if (!isDeleteKey) return false
      if (event.target && event.target.value) return false

      const targetChoice = event.target && event.target.closest
        ? event.target.closest('.ant-select-selection__choice')
        : null
      if (targetChoice && this.getLockedOptionTitles().includes(targetChoice.getAttribute('title'))) {
        return true
      }

      const values = this[SOURCE] || []
      const lastSelectedValue = values[values.length - 1]
      return this.getLockedOptionValues().includes(lastSelectedValue)
    },
    emitChange (newValue, option) {
      const nextValue = this.getChangeValueWithLockedOptions(newValue)
      this.$emit(EVENT, nextValue, option)
      if (!this.isSameArrayValue(nextValue, newValue)) {
        this.syncLockedSelectedChoices()
      }
    },
    syncLockedSelectedChoices () {
      this.$nextTick(() => {
        const root = this.$el
        if (!root || !root.querySelectorAll) return

        const lockedChoiceTitles = new Set(this.getLockedOptionTitles())
        const choices = root.querySelectorAll('.ant-select-selection__choice')
        choices.forEach(choice => {
          const shouldLockRemove = lockedChoiceTitles.has(choice.getAttribute('title'))
          choice.classList.toggle('hkl-select__choice-locked', shouldLockRemove)
          const removeIcon = choice.querySelector('.ant-select-selection__choice__remove')
          if (removeIcon) {
            removeIcon.style.display = shouldLockRemove ? 'none' : ''
          }
        })
      })
    },
  },
}

export default HKLSelect
</script>
<style lang="scss">
@import '~styles/_settings.scss';

$option-height: 34px;

.hkl-select .ant-select-selection-selected-value,
.hkl-select .ant-select-search__field,
.ant-select-dropdown.hkl-select-dropdown .ant-select-dropdown-menu-item-selected {
  font-weight: normal !important;
}

// dropdown
.ant-select-dropdown {
  border-radius: 0;
}
.ant-select-dropdown-menu {
  margin-left: 0;
}
.ant-select-dropdown.hkl-select-dropdown {
  .ant-select-dropdown-menu,
  .ant-select-dropdown-menu-root {
    max-height: var(--hkl-select-dropdown-max-height, 208px) !important;
    overflow-y: var(--hkl-select-dropdown-overflow-y, auto) !important;
  }

  &.ant-select-dropdown--empty {
    .ant-select-dropdown-menu,
    .ant-select-dropdown-menu-root {
      max-height: none !important;
      overflow-y: hidden !important;
    }

    .ant-select-dropdown-menu-item {
      min-height: auto;
    }
  }

  &.hkl-select-dropdown--no-match-width {
    width: max-content !important;
    min-width: 0 !important;

    .ant-select-dropdown-menu,
    .ant-select-dropdown-menu-root {
      width: max-content;
      min-width: 0;
    }
  }

  .hkl-select__option-with-hint {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    min-width: 0;
  }

  .hkl-select__option-label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .hkl-select__option-hint {
    flex-shrink: 0;
    padding-left: 4px;
    font-weight: bold;
  }
}
.ant-select-dropdown-menu-item,
.ant-select-dropdown-menu-item-group-title {
  min-height: $option-height;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-transform: none;
}

.ant-select-dropdown-menu-item {
  min-height: $option-height;
  transition: none;
  border-top: solid 1px #62719164;
  text-align: left;
  text-transform: none;
  font-weight: normal;
  color: #35495E;

  &:first-child {
    border-top-width: 0;
  }
  &-active:not(.ant-select-dropdown-menu-item-disabled):not(.ant-select-dropdown-menu-item-selected),
  &:hover:not(.ant-select-dropdown-menu-item-disabled):not(.ant-select-dropdown-menu-item-selected) {
    background-color: #F3F3F3 !important;
    color: #35495E;
  }

  &-group-title {
    padding-left: 20px;
    background-color: #f3f3f3;
    color: #35495E;
    font-weight: bold;
  }

  &-selected {
    background-color: #6e7a91 !important;
    color: #fff;
    font-weight: normal;

    .ant-select-selected-icon {
      color: #35495E !important;
    }
  }

  &-selected.ant-select-dropdown-menu-item-active:not(.ant-select-dropdown-menu-item-disabled),
  &-selected:hover:not(.ant-select-dropdown-menu-item-disabled) {
    background-color: #8793a8 !important;
    color: #fff;
    font-weight: normal;

    .ant-select-selected-icon {
      color: #35495E !important;
    }
  }

  &-disabled.ant-select-dropdown-menu-item-selected {
    background-color: #6e7a91 !important;
    color: #fff;
    font-weight: normal;
    cursor: default;
  }
}

.ant-select-dropdown.hkl-select-dropdown--no-match-width {
  .ant-select-dropdown-menu-item,
  .ant-select-dropdown-menu-item-group-title {
    overflow: visible;
    text-overflow: clip;
    white-space: nowrap;
  }
}

.ant-select-dropdown.hkl-select-dropdown.ant-select-dropdown--multiple {
  .ant-select-dropdown-menu-item:hover:not(.ant-select-dropdown-menu-item-selected) .ant-select-selected-icon {
    color: transparent !important;
  }

  .ant-select-dropdown-menu-item-selected .ant-select-selected-icon,
  .ant-select-dropdown-menu-item-selected:hover .ant-select-selected-icon {
    color: #35495E !important;
  }
}

.ant-select-dropdown.hkl-select-dropdown .ant-select-dropdown-menu-item.hkl-select__tag-option {
  padding: 10px 12px;
  font-family: Verdana;
  font-size: 14px;
  font-weight: normal;
  line-height: 16px;

  .hkl-select__option-with-hint {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    min-width: 0;
  }

  .hkl-select__option-label {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .hkl-select__option-hint {
    flex-shrink: 0;
    padding-left: 0;
    font-size: 14px;
    font-weight: normal;
    line-height: 16px;
    background: transparent;
    color: inherit;
  }

  &.ant-select-dropdown-menu-item-active,
  &:hover {
    .hkl-select__option-hint {
      background: transparent;
      color: inherit;
    }
  }
}

.ant-select-dropdown.hkl-select-dropdown .ant-select-dropdown-menu-item.hkl-select__display-only-option {
  display: none !important;
}

.ant-select-dropdown.hkl-select-dropdown.general-info-floor-dropdown {
  .ant-select-dropdown-menu-item,
  .ant-select-dropdown-menu-item-group-title {
    min-height: $option-height;
    padding: 10px 12px;
    font-size: 14px;
    line-height: 16px;
    text-transform: none;
  }

  .ant-select-dropdown-menu-item {
    font-weight: normal;
  }

  .ant-select-dropdown-menu-item-group-title {
    background-color: #ededed;
    color: #a6a6a6;
    font-weight: normal;
  }
}

.hkl-select.error-highlight,
.hkl-select.error-highlight-floor,
.hkl-select.fusion-input-check-is-invalid-input,
.hkl-select.required-error-highlight,
.hkl-select:has(input.error-highlight),
.hkl-select:has(input.error-highlight-floor),
.hkl-select:has(input.fusion-input-check-is-invalid-input),
.hkl-select:has(input.is-invalid-input),
.error-highlight .hkl-select,
.error-highlight-floor .hkl-select,
.fusion-input-check-is-invalid-input .hkl-select,
.required-error-highlight .hkl-select {
  background-color: $error-light-red !important;

  .ant-select-selection,
  .ant-select-selection__rendered,
  .ant-select-search,
  .ant-select-search__field {
    background-color: $error-light-red !important;
  }
}
</style>
<style lang="scss" scoped>
@import '~styles/_settings.scss';

$selection-height: 38px;
$tag-height: 30px;
.hkl-select {
  box-sizing: border-box;
  width: 100%;
  min-width: 100px;
  border: solid 1px #B5B5B5;

  &.hkl-select_multiple-search-empty ::v-deep .ant-select-selection--multiple .ant-select-search--inline {
    width: 1px !important;
    min-width: 0;
    margin: 0;
    overflow: hidden;
  }

  &.hkl-select_multiple:not(.no-border) {
    border: 0;

    ::v-deep .ant-select-selection--multiple {
      border: solid 1px #B5B5B5 !important;
      box-sizing: border-box;
      min-height: $selection-height + 2px;
    }
  }

  &.columns {
    min-width: 100px;
    min-height: 40px;
  }

  @for $i from 1 through 12 {
    &.columns.small-#{$i} {
      width: percentage($i / 12);
    }
  }

  @media screen and (min-width: 40em) {
    @for $i from 1 through 12 {
      &.columns.medium-#{$i} {
        width: percentage($i / 12);
      }
    }
  }

  @media screen and (min-width: 64em) {
    @for $i from 1 through 12 {
      &.columns.large-#{$i} {
        width: percentage($i / 12);
      }
    }
  }

  ::v-deep .hkl-select__choice-locked {
    padding: 0 10px;

    .ant-select-selection__choice__remove {
      display: none !important;
    }
  }

  ::v-deep .ant-select-selection {
    border-width: 0 !important;
    box-shadow: none;
    width: 100%;
    min-height: $selection-height;
    border-radius: 0;
    transition: none;
    .ant-select-selection__rendered {
      color: #354961;
      margin-right: 26px;
      min-height: $selection-height;
      line-height: $selection-height;
      overflow: hidden;
    }
    .ant-select-selection-selected-value,
    .ant-select-selection__placeholder {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 100%;
      display: inline-block;
      font-weight: normal;
    }
    .ant-select-search__field {
      font-family: inherit;
      font-weight: normal;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .ant-select-selection__placeholder {
      font-weight: normal;
    }
    .ant-select-search {
      height: $tag-height;
      line-height: $tag-height;
    }
    &--single {
      .ant-select-search {
        height: 100%;
        line-height: 100%;
      }
    }
    .ant-select-selection__choice {
      display: flex;
      align-items: center;
      margin-right: 2px;
      padding: 0 17px 0 8px;
      min-height: $tag-height;
      min-width: 54px;
      max-width: calc(100% - 4px);
      background: #6e7a91;
      line-height: $tag-height;
      color: #fff;
      font-weight: normal;
      text-transform: none;
      &__content {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      &__remove {
        flex-shrink: 0;
        right: 1px;
        font-size: 15px;
        font-weight: bolder;
      }
    }
    &--multiple {
      padding: 0;

      .ant-select-selection__rendered {
        margin-bottom: 0;
        margin-left: 5px;
      }

      .ant-select-selection__choice {
        box-sizing: border-box;
        height: $selection-height;
        margin-top: 0;
        margin-right: 0;
        min-height: $selection-height;
        line-height: $selection-height;
      }
    }

    .ant-select-arrow {
      color: #35495E;
      top: 50%;
      right: 9px;
      font-size: 16px;
    }

  }

  &.hkl-select_multiple-has-value {
    ::v-deep .ant-select-selection--multiple .ant-select-selection__rendered {
      margin-left: 0;
    }
  }

  &.ant-select-combobox.ant-select-show-arrow {
    ::v-deep .ant-select-selection__rendered {
      margin-right: 32px;
    }

    ::v-deep .ant-select-search--inline {
      width: calc(100% - 32px);
    }

    ::v-deep .ant-select-arrow {
      display: inline-flex !important;
      align-items: center;
      justify-content: center;
      z-index: 2;
    }

    ::v-deep .ant-select-arrow-icon,
    ::v-deep .ant-select-arrow-icon svg {
      display: block;
    }
  }

  &.hkl-select_search-input-flush {
    ::v-deep .ant-select-selection--single .ant-select-search--inline {
      left: -11px;
      width: calc(100% + 11px);
    }
  }

  &_right-align {
    ::v-deep {
      .ant-select-selection__placeholder {
        text-align: right;
      }
      .ant-select-selection-selected-value {
        float: right;
        margin-right: 5px;
      }
    }
    & + ::v-deep div {
      .ant-select-dropdown-menu-item {
        text-align: right;
      }
    }
  }
}
.hkl-select.no-border ::v-deep{
  border: none;
  .ant-select-selection {
    .ant-select-arrow {
      top: 43%;
    }

  }
}
.ant-select-disabled ::v-deep {
  opacity: 1;
  background: #ededed;

  .ant-select-selection {
    background: #ededed;
  }

  .ant-select-selection__rendered,
  .ant-select-selection-selected-value,
  .ant-select-selection__placeholder {
    color: #0C3A58;
    opacity: 1;
  }

  .ant-select-selection__rendered  .ant-select-selection__choice{
      background: #e0e0e0;
      color: #0C3A58;
  }
}
.fusion-input-check-is-invalid-input {
  background-color: $error-light-red !important;

  ::v-deep .ant-select-selection {
    background-color: $error-light-red !important;
  }
}
.error-highlight,
.error-highlight-floor {
  background-color: $error-light-red !important;

  ::v-deep .ant-select-selection,
  ::v-deep .ant-select-selection__rendered,
  ::v-deep .ant-select-search,
  ::v-deep .ant-select-search__field {
    background-color: $error-light-red !important;
  }
}
.hkl-select.flex-theme ::v-deep{
  .ant-select-selection {
    display: flex;
    align-items: center;
    .ant-select-selection__rendered {
      min-height: unset;
      line-height: unset;
    }
    .ant-select-arrow {
      position: relative;
      top: 2px;
      right: 0;
      margin-top: 0;
    }
  }
}
</style>
